import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { createContext, useContext, useEffect, useState } from 'react';
import { useCentralizedProviderData } from '@crehana/auth';
import { CREHANA_BRANDING } from '@crehana/constants/branding';
import { jsx as _jsx } from "react/jsx-runtime";
var OrganizationBrandingContext = /*#__PURE__*/createContext(undefined);

/**
 * The main responsibility of this provider is to apply the branding styles and favicon to the page.
 * It doesn't fetch the data by itself, instead it reuses the branding data from the `useCentralizedProvider` hook.
 * It also exposes helpers and methods to modify the current branding data.
 */
export var OrganizationBrandingProvider = function OrganizationBrandingProvider(_ref) {
  var children = _ref.children;
  // we can't use the `useCentralizedMe` because it's above of the AuthGuard.
  var _useCentralizedProvid = useCentralizedProviderData(),
    currentPageOrganization = _useCentralizedProvid.currentPageOrganization,
    loading = _useCentralizedProvid.loading;
  var _useState = useState(CREHANA_BRANDING),
    _useState2 = _slicedToArray(_useState, 2),
    branding = _useState2[0],
    setBranding = _useState2[1];
  useEffect(function () {
    if (loading || !currentPageOrganization) {
      return;
    }
    applyBrandingStylesV1(currentPageOrganization.branding.paletteColors.v1);
    applyFavicon(currentPageOrganization.branding.imageFavicon.absoluteUrl);
    setBranding(currentPageOrganization.branding);
  }, [currentPageOrganization, loading]);
  var updateBrandingData = function updateBrandingData(branding) {
    var _branding$paletteColo, _branding$imageFavico;
    setBranding(function (prev) {
      return _objectSpread(_objectSpread({}, prev), branding);
    });
    if ((_branding$paletteColo = branding.paletteColors) !== null && _branding$paletteColo !== void 0 && _branding$paletteColo.v1) {
      applyBrandingStylesV1(branding.paletteColors.v1);
    }
    if ((_branding$imageFavico = branding.imageFavicon) !== null && _branding$imageFavico !== void 0 && _branding$imageFavico.absoluteUrl) {
      applyFavicon(branding.imageFavicon.absoluteUrl);
    }
  };
  var context = {
    updateBrandingData: updateBrandingData,
    branding: branding
  };
  return /*#__PURE__*/_jsx(OrganizationBrandingContext.Provider, {
    value: context,
    children: children
  });
};

/**
 *
 * This hook exposes helpers to apply the branding styles and favicon to the page. It's mainly used in the module customization after the user has changed the palette colors and favicon.
 *
 * If you want to access to the branding data of the current organization, you can use the `useCentralizedProvider` hook. Example: `const { currentPageOrganization: { branding } } = useCentralizedProvider();`.
 */
export var useOrganizationBranding = function useOrganizationBranding() {
  var context = useContext(OrganizationBrandingContext);
  if (!context) {
    throw new Error('Missing OrganizationBrandingContext');
  }
  return context;
};
function applyFavicon(faviconUrl) {
  try {
    // Remove all existing favicon-related links
    var existingFavicons = document.querySelectorAll("link[rel*='icon'], link[rel*='apple-touch-icon']");
    existingFavicons.forEach(function (link) {
      return link.remove();
    });

    // 1. Main favicon (32x32)
    var favicon32 = document.createElement('link');
    favicon32.id = 'favicon';
    favicon32.rel = 'icon';
    favicon32.type = 'image/png';
    favicon32.setAttribute('sizes', '32x32');
    favicon32.href = faviconUrl;
    document.head.appendChild(favicon32);

    // 2. Shortcut icon (backward compatibility)
    var shortcutIcon = document.createElement('link');
    shortcutIcon.id = 'lnkfavicon';
    shortcutIcon.rel = 'shortcut icon';
    shortcutIcon.type = 'image/png';
    shortcutIcon.href = faviconUrl;
    document.head.appendChild(shortcutIcon);

    // 3. 64x64 for high-DPI displays (browser will scale from 32x32)
    var favicon64 = document.createElement('link');
    favicon64.rel = 'icon';
    favicon64.type = 'image/png';
    favicon64.setAttribute('sizes', '64x64');
    favicon64.href = faviconUrl;
    document.head.appendChild(favicon64);

    // 4. Apple touch icon (reuses the same 32x32, iOS will scale it)
    var appleTouchIcon = document.createElement('link');
    appleTouchIcon.rel = 'apple-touch-icon';
    appleTouchIcon.href = faviconUrl;
    document.head.appendChild(appleTouchIcon);
  } catch (error) {
    console.error('Error applying favicon:', error);
  }
}
function applyBrandingStylesV1(colors) {
  try {
    var root = document.documentElement;

    // Apply CSS custom properties to :root
    // New design system (design system v2)
    root.style.setProperty('--cds-brand-90', colors.darkest);
    root.style.setProperty('--cds-brand-50', colors.dark);
    root.style.setProperty('--cds-brand-40', colors.base);
    root.style.setProperty('--cds-brand-30', colors.light);
    root.style.setProperty('--cds-brand-10', colors.lightest);

    // @crehana/compass.ui (design system v1)
    root.style.setProperty('--color-primary-light-700', colors.darkest);
    root.style.setProperty('--color-primary-light-500', colors.dark);
    root.style.setProperty('--color-primary-light-400', colors.base);
    root.style.setProperty('--color-primary-light-300', colors.light);
    root.style.setProperty('--color-primary-light-100', colors.lightest);
    // @crehana/ui (legacy)
    root.style.setProperty('--color-primary-dark', colors.darkest);
    root.style.setProperty('--color-primary-lighter', colors.light);
    root.style.setProperty('--color-primary-light', colors.base);
    root.style.setProperty('--color-primary-main', colors.dark);
  } catch (error) {
    console.error('Error applying branding styles:', error);
  }
}